// src/services/auth/Auth0AuthService.ts

import { Auth0Client } from "@auth0/auth0-spa-js";
import {
  fetchUserProfileFromBackend,
} from "@/services/queries/userProfileQueries";
import type {
  Auth0ClientOptions,
  User as Auth0User,
} from "@auth0/auth0-spa-js";
import type { AuthService } from "./AuthService";
import { acceptTermsAndConditions } from "../terms";
import type { User, AuthToken, Auth0Config } from "@/types/auth.types";

/**
 * Auth0 implementation of the AuthService interface.
 *
 * This service wraps the Auth0 SPA SDK and provides a clean, provider-agnostic interface.
 * All Auth0-specific logic is contained in this file, making it easy to swap providers.
 *
 * RESPONSIBILITIES:
 * - Initialize Auth0 client
 * - Handle OAuth redirect callback
 * - Manage authentication state
 * - Transform Auth0 user → generic User type
 * - Handle token refresh automatically
 * - Notify subscribers of auth state changes
 *
 * TOKEN MANAGEMENT:
 * Auth0 SDK handles token refresh automatically via `getAccessTokenSilently()`.
 * We just call it whenever we need a token, and Auth0 ensures it's fresh.
 */
export class Auth0AuthService implements AuthService {
  private client: Auth0Client | null = null;
  private config: Auth0Config;
  private currentUser: User | null = null;
  private authenticated: boolean = false;
  private loading: boolean = true;
  private loggingOut: boolean = false;
  private error: string | null = null;
  private listeners: Set<(user: User | null) => void> = new Set();

  // Token refresh buffer (refresh 5 minutes before expiry)
  private static readonly TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;
  private tokenRefreshTimer: number | null = null;

  constructor(config: Auth0Config) {
    this.config = config;
  }

  /**
   * Initialize Auth0 client and restore session if available
   */
  async initialize(): Promise<void> {
    try {
      this.loading = true;
      this.error = null;

      // Create Auth0 client
      const options: Auth0ClientOptions = {
        domain: this.config.domain,
        clientId: this.config.clientId,
        authorizationParams: {
          redirect_uri: this.config.redirectUri,
          ...(this.config.audience && { audience: this.config.audience }),
          scope: this.config.scope,
        },
        cacheLocation: this.config.cacheLocation,
        useRefreshTokens: this.config.useRefreshTokens,
      };

      this.client = new Auth0Client(options);

      // Check if we're returning from OAuth callback
      const query = window.location.search;
      if (query.includes("code=") && query.includes("state=")) {
        await this.handleCallback();
      }

      // Check if user is authenticated
      const isAuthenticated = await this.client.isAuthenticated();

      if (isAuthenticated) {
        await this.loadUser();
      }

      this.loading = false;
      this.notifyListeners();
    } catch (err) {
      this.loading = false;
      this.error = err instanceof Error ? err.message : "Failed to initialize Auth0";
      console.error("Auth0 initialization error:", err);
      this.notifyListeners();
    }
  }

  /**
   * Load user data from Auth0 and transform to generic User type
   */
  private async loadUser(): Promise<void> {
    if (!this.client) {
      throw new Error("Auth0 client not initialized");
    }

    try {
      const auth0User = await this.client.getUser<Auth0User>();

      if (!auth0User) {
        this.authenticated = false;
        this.currentUser = null;
        return;
      }

      // 4. Transform to our User type
      const user = this.transformAuth0User(auth0User);
      
      // 5. Fetch authoritative user data from backend
      try {
        const token = await this.client.getTokenSilently();
        const backendProfile = await fetchUserProfileFromBackend(token);
        
        // Merge backend data into user object
        if (backendProfile) {
          console.log("[Auth0] Merging backend profile data:", backendProfile);
          
          // Update terms acceptance from backend source of truth
          if (backendProfile.termsAccepted !== undefined) {
            user.termsAccepted = backendProfile.termsAccepted;
          }
          
          if (backendProfile.termsVersion) {
            user.termsVersion = backendProfile.termsVersion;
          }
          
          // Update other fields if present
          if (backendProfile.username) user.username = backendProfile.username;
          if (backendProfile.firstName) user.firstName = backendProfile.firstName;
          if (backendProfile.lastName) user.lastName = backendProfile.lastName;
        }
      } catch (err) {
        // If we can't get a token, we shouldn't consider the user fully authenticated
        // with backend data. However, for "Missing Refresh Token" or "Login required",
        // we must fail completely to force a re-login, otherwise we get into a state
        // where we have a user object but no valid token to make requests (like accepting terms).
        const errorMessage = err instanceof Error ? err.message : String(err);
        if (
          errorMessage.includes("Missing Refresh Token") ||
          errorMessage.includes("Login required")
        ) {
          console.error("[Auth0] Critical auth error, forcing logout:", err);
          throw err;
        }

        console.error("[Auth0] Failed to fetch backend profile, using Auth0 data only:", err);
      }

      this.currentUser = user;
      this.authenticated = true;

      // Set up token refresh
      await this.setupTokenRefresh();
    } catch (err) {
      console.error("Failed to load user:", err);
      this.authenticated = false;
      this.currentUser = null;
    }
  }

  /**
   * Transform Auth0 user object to generic User type
   * Maps Auth0-specific fields to our universal User interface
   *
   * TODO: Enhance Auth0 metadata fetching
   * Currently reads from user_metadata and app_metadata in the ID token.
   * Future improvements:
   * 1. Use Auth0 Management API to fetch/update user_metadata and app_metadata
   * 2. Implement subscription tier management via Auth0 Actions/Rules
   * 3. Add profile update methods that write back to Auth0
   * 4. Consider using Auth0 Organizations for enterprise tier management
   */
  private transformAuth0User(auth0User: Auth0User): User {
    // Auth0 stores custom data in user_metadata and app_metadata
    const userMetadata = (auth0User["user_metadata"] || {}) as Record<string, unknown>;
    const appMetadata = (auth0User["app_metadata"] || {}) as Record<string, unknown>;

    // TODO: Fetch subscription tier from Auth0 app_metadata (managed by Auth0 Rules/Actions)
    const tier = (appMetadata["tier"] as "free" | "hobby" | "pro" | "enterprise" | undefined) || "free";
    const username = userMetadata["username"] as string | undefined;
    const firstName = userMetadata["firstName"] as string | undefined;
    const lastName = userMetadata["lastName"] as string | undefined;
    const status = appMetadata["status"] as string | undefined;
    const subscriptionExpiresAt = appMetadata["subscriptionExpiresAt"] as string | undefined;
    const subscriptionAutoRenew = appMetadata["subscriptionAutoRenew"] as boolean | undefined;
    const termsAccepted = (appMetadata["termsAccepted"] as boolean) || false;
    const createdAtMeta = userMetadata["createdAt"] as string | undefined;

    return {
      id: auth0User.sub || "",
      ...(auth0User.sub && { auth0Id: auth0User.sub }),
      email: auth0User.email || "",
      ...(auth0User.email_verified !== undefined && {
        emailVerified: auth0User.email_verified,
      }),

      // Name fields - Auth0 typically provides 'name', but support username too
      ...(auth0User.name && { name: auth0User.name }),
      ...(username && { username }),
      ...(firstName && { firstName }),
      ...(lastName && { lastName }),
      ...(auth0User.picture && { picture: auth0User.picture }),

      // Status
      ...(status && { status }),

      // Subscription data from app_metadata (with defaults)
      tier,
      subscriptionActive: (appMetadata["subscriptionActive"] as boolean) || false,
      ...(subscriptionExpiresAt && { subscriptionExpiresAt }),
      ...(subscriptionAutoRenew !== undefined && { subscriptionAutoRenew }),
      
      // Terms and Conditions
      termsAccepted,

      // Timestamps
      createdAt:
        createdAtMeta ||
        (auth0User.updated_at as string) ||
        new Date().toISOString(),
      updatedAt:
        (auth0User.updated_at as string) || new Date().toISOString(),
    };
  }

  /**
   * Set up automatic token refresh before expiry
   */
  private async setupTokenRefresh(): Promise<void> {
    // Clear existing timer
    if (this.tokenRefreshTimer !== null) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }

    try {
      const tokenWithExpiry = await this.getTokenWithExpiry();

      if (!tokenWithExpiry) {
        return;
      }

      const now = Date.now();
      const timeUntilExpiry = tokenWithExpiry.expiresAt - now;
      const timeUntilRefresh = timeUntilExpiry - Auth0AuthService.TOKEN_REFRESH_BUFFER_MS;

      if (timeUntilRefresh > 0) {
        this.tokenRefreshTimer = window.setTimeout(async () => {
          console.log("[Auth0AuthService] Token refresh timer triggered");
          await this.getToken(); // This will refresh the token
          await this.setupTokenRefresh(); // Set up next refresh
        }, timeUntilRefresh);

        console.log(
          `[Auth0AuthService] Token refresh scheduled in ${Math.round(timeUntilRefresh / 1000 / 60)} minutes`
        );
      }
    } catch (err) {
      console.error("[Auth0AuthService] Failed to setup token refresh:", err);
    }
  }

  /**
   * Handle OAuth callback after redirect from Auth0
   */
  async handleCallback(): Promise<void> {
    if (!this.client) {
      throw new Error("Auth0 client not initialized");
    }

    try {
      // Process the callback
      await this.client.handleRedirectCallback();

      // Remove code and state from URL
      window.history.replaceState(
        {},
        document.title,
        window.location.pathname
      );

      // Load user data
      await this.loadUser();
      this.notifyListeners();
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to handle OAuth callback";
      console.error("Auth0 callback error:", err);
      throw err;
    }
  }

  isAuthenticated(): boolean {
    return this.authenticated;
  }

  isLoading(): boolean {
    return this.loading;
  }

  isLoggingOut(): boolean {
    return this.loggingOut;
  }

  getUser(): User | null {
    return this.currentUser;
  }

  getError(): string | null {
    return this.error;
  }

  /**
   * Initiate login by redirecting to Auth0 hosted login page
   */
  async login(): Promise<void> {
    if (!this.client) {
      throw new Error("Auth0 client not initialized");
    }

    try {
      await this.client.loginWithRedirect({
        authorizationParams: {
          redirect_uri: this.config.redirectUri,
        },
      });
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Login failed";
      console.error("Auth0 login error:", err);
      throw err;
    }
  }

  /**
   * Logout user and clear session
   */
  async logout(): Promise<void> {
    if (!this.client) {
      throw new Error("Auth0 client not initialized");
    }

    try {
      // Set logging out flag to prevent redirects during logout
      this.loggingOut = true;

      // Clear token refresh timer
      if (this.tokenRefreshTimer !== null) {
        clearTimeout(this.tokenRefreshTimer);
        this.tokenRefreshTimer = null;
      }

      // Clear local state
      this.authenticated = false;
      this.currentUser = null;

      // Notify listeners before logout (so they can clean up)
      this.notifyListeners();

      // Logout from Auth0 (will redirect)
      await this.client.logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Logout failed";
      console.error("Auth0 logout error:", err);
      throw err;
    }
  }

  /**
   * Get a valid access token (Auth0 handles refresh automatically)
   */
  async getToken(): Promise<string> {
    if (!this.client) {
      throw new Error("Auth0 client not initialized");
    }

    if (!this.authenticated) {
      throw new Error("User not authenticated");
    }

    try {
      // Auth0 SDK automatically refreshes the token if needed
      const token = await this.client.getTokenSilently();
      return token;
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to get access token";
      console.error("Failed to get access token:", err);
      throw err;
    }
  }

  /**
   * Get token with expiry information
   */
  async getTokenWithExpiry(): Promise<AuthToken | null> {
    if (!this.authenticated) {
      return null;
    }

    try {
      const token = await this.getToken();

      // Decode JWT to get expiry (without verification, just for expiry time)
      const expiry = this.decodeJwtExpiry(token);

      if (!expiry) {
        // Fallback: assume 1 hour expiry if decode fails
        const expiresAt = Date.now() + 60 * 60 * 1000;
        return { accessToken: token, expiresAt };
      }

      return {
        accessToken: token,
        expiresAt: expiry * 1000, // Convert seconds to milliseconds
      };
    } catch (err) {
      console.error("Failed to get token with expiry:", err);
      return null;
    }
  }

  /**
   * Decode JWT to extract expiry time (exp claim)
   * Note: This does NOT verify the signature, only extracts the expiry
   */
  private decodeJwtExpiry(token: string): number | null {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) {
        return null;
      }

      const payload = parts[1];
      if (!payload) {
        return null;
      }

      // Base64url decode
      const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
      const paddedBase64 = base64.padEnd(
        base64.length + ((4 - (base64.length % 4)) % 4),
        "="
      );
      const jsonPayload = atob(paddedBase64);
      const decoded = JSON.parse(jsonPayload) as { exp?: number };

      return decoded.exp || null;
    } catch (err) {
      console.warn("Failed to decode JWT:", err);
      return null;
    }
  }

  /**
   * Subscribe to auth state changes
   */
  onAuthStateChange(callback: (user: User | null) => void): () => void {
    this.listeners.add(callback);

    // Immediately call with current state
    callback(this.currentUser);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all listeners of auth state change
   */
  private notifyListeners(): void {
    this.listeners.forEach((callback) => {
      try {
        callback(this.currentUser);
      } catch (err) {
        console.error("Error in auth state change listener:", err);
      }
    });
  }

  /**
   * Force refresh user data from Auth0
   * This forces a fresh ID token fetch to get updated user_metadata
   */
  async refreshUser(): Promise<User | null> {
    console.log("[Auth0AuthService] 🔄 refreshUser() called");

    if (!this.authenticated) {
      console.log("[Auth0AuthService] ❌ Not authenticated, returning null");
      return null;
    }

    if (!this.client) {
      console.error("[Auth0AuthService] ❌ Auth0 client not initialized");
      return this.currentUser;
    }

    try {
      console.log("[Auth0AuthService] 📥 Forcing fresh ID token fetch from Auth0...");

      // Force a fresh token fetch to get updated user claims
      // getTokenSilently with cacheMode: 'off' forces Auth0 to fetch fresh tokens
      await this.client.getTokenSilently({ cacheMode: 'off' });

      console.log("[Auth0AuthService] ✅ Fresh tokens fetched!");
      console.log("[Auth0AuthService] 📥 Loading updated user data...");

      // Now load the updated user data from the fresh ID token
      await this.loadUser();

      console.log("[Auth0AuthService] ✅ User data refreshed:", {
        username: this.currentUser?.username,
        name: this.currentUser?.name,
        email: this.currentUser?.email,
      });

      // Notify all listeners (including Redux) of the updated user
      this.notifyListeners();

      return this.currentUser;
    } catch (err) {
      console.error("[Auth0AuthService] ❌ Failed to refresh user:", err);
      if (err instanceof Error) {
        console.error("[Auth0AuthService] Error message:", err.message);
        console.error("[Auth0AuthService] Error stack:", err.stack);
      }
      return this.currentUser;
    }
  }

  /**
   * Update user data directly from external source (e.g., backend API)
   * This bypasses Auth0 ID token and directly updates the current user
   * Useful when backend fetches fresh data from Auth0 Management API
   */
  updateUserData(userData: Partial<User>): void {
    console.log("[Auth0AuthService] 📝 updateUserData() called with:", userData);

    if (!this.currentUser) {
      console.warn("[Auth0AuthService] ⚠️ No current user to update");
      return;
    }

    // Merge the new data with existing user data
    this.currentUser = {
      ...this.currentUser,
      ...userData,
      // Always preserve the ID
      id: this.currentUser.id,
    };

    console.log("[Auth0AuthService] ✅ User data updated:", {
      username: this.currentUser.username,
      name: this.currentUser.name,
      email: this.currentUser.email,
    });

    // Notify all listeners (including Redux) of the updated user
    this.notifyListeners();
  }

  /**
   * Accept Terms and Conditions
   * Updates backend and local user state
   */
  async acceptTerms(): Promise<void> {
    if (!this.currentUser) return;

    try {
      const token = await this.getToken();
      await acceptTermsAndConditions(token);
      
      // Update local state
      this.updateUserData({ 
        termsAccepted: true,
        termsVersion: import.meta.env["VITE_TERMS_VERSION"]
      });
    } catch (err) {
      console.error("Failed to accept terms:", err);
      throw err;
    }
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    if (this.tokenRefreshTimer !== null) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
    this.listeners.clear();
  }
}
